import { Button, Radio, Select, SelectItem, Skeleton, Stack, Title } from "@skailer-com/uikit";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useImportDataEditContext } from "@/pages/settings/import-data/edit/import-data-edit.types.ts";
import { useImportDataParse } from "@/queries/import-data.query.ts";
import {
  ApiImportDataType,
  ApiImportFieldUpdateBehavior,
  ApiImportGlobalUpdateBehavior,
} from "@/types/import-data.types.ts";
import { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

export function ImportDataCommonSettings() {
  const { t } = useTranslation();
  const { truncate } = useFormatter();
  const {
    importId,
    importType,
    matchingBy,
    setMatchingBy,
    selectedCustomTableId,
    setSelectedCustomTableId,
    setSelectedCustomTableName,
    importBehavior,
    defaultUpdateBehavior,
    setDefaultUpdateBehavior,
    importPayload,
    registerOnNext,
    goToNextStep,
    nextButtonText,
  } = useImportDataEditContext();
  const { data: parseOverview, isPending: isParseLoading } = useImportDataParse(importId);

  const matchByOptions = useMemo(
    (): SelectItem[] =>
      parseOverview?.columns
        .filter((col) => col.sample_values.length > 0)
        .map((col) => ({
          id: col.column_index,
          title: col.header,
          caption: col.sample_values.map((v) => truncate(v, 80)).join(", "),
        })) ?? [],
    [parseOverview?.columns, truncate]
  );

  const customTableOptions = useMemo(
    (): SelectItem[] =>
      parseOverview?.possible_tables?.map((table) => ({
        id: String(table.table_id),
        title: table.table_name,
      })) ?? [],
    [parseOverview?.possible_tables]
  );

  const getBehaviorInformation = useCallback(
    (behavior: ApiImportGlobalUpdateBehavior): { title: string; caption: string } => ({
      title: t(`import_data:behavior_${behavior}`),
      caption: t(`import_data:behavior_${behavior}_caption`),
    }),
    [t]
  );

  useEffect(() => {
    if (!parseOverview) return;

    const defaultMatchingBy =
      importPayload?.matching_by ??
      parseOverview.matching_by ??
      (matchByOptions[0]?.id as number | undefined) ??
      null;

    if (matchingBy === null || !matchByOptions.some((option) => option.id === matchingBy)) {
      setMatchingBy(defaultMatchingBy);
    }
  }, [parseOverview, matchByOptions, matchingBy, importPayload, setMatchingBy]);

  useEffect(() => {
    if (importType !== ApiImportDataType.custom_table || !parseOverview) return;

    const savedTableId =
      importPayload?.columns[0]?.table_id ?? importPayload?.custom_tables_keys[0]?.table_id ?? null;
    const defaultTableId = savedTableId ?? parseOverview.possible_tables?.[0]?.table_id ?? null;

    if (
      !selectedCustomTableId ||
      !customTableOptions.some((option) => option.id === String(selectedCustomTableId))
    ) {
      setSelectedCustomTableId(defaultTableId);
      const tableName =
        customTableOptions.find((opt) => opt.id === String(defaultTableId))?.title ?? null;
      setSelectedCustomTableName(tableName);
    }
  }, [
    importType,
    parseOverview,
    customTableOptions,
    selectedCustomTableId,
    setSelectedCustomTableId,
    setSelectedCustomTableName,
    importPayload,
  ]);

  const handleNext = useCallback(() => {
    if (matchingBy === null) {
      return;
    }

    if (importType === ApiImportDataType.custom_table && !selectedCustomTableId) {
      return;
    }

    goToNextStep();
  }, [matchingBy, importType, selectedCustomTableId, goToNextStep]);

  useLayoutEffect(() => {
    registerOnNext(handleNext);
    return () => registerOnNext(null);
  }, [handleNext, registerOnNext]);

  if (isParseLoading) {
    return (
      <Stack gap="sm">
        <Stack className="card-panel" gap="2xl">
          <Stack gap="sm">
            <Skeleton className="w-48 h-7" />
            <Skeleton className="w-72 h-5" />
          </Stack>
        </Stack>
        <Stack className="card-panel" gap="2xl">
          <Stack gap="sm">
            <Skeleton className="w-48 h-7" />
            <Skeleton className="w-72 h-5" />
          </Stack>
          <Skeleton className="w-full h-14" />
        </Stack>
        <Stack className="card-panel" gap="2xl">
          <Stack gap="sm">
            <Skeleton className="w-48 h-7" />
            <Skeleton className="w-72 h-5" />
          </Stack>
          <Skeleton className="w-full h-14" />
        </Stack>
        {importType === ApiImportDataType.custom_table && (
          <Stack className="card-panel" gap="2xl">
            <Stack gap="sm">
              <Skeleton className="w-48 h-7" />
              <Skeleton className="w-72 h-5" />
            </Stack>
            <Skeleton className="w-full h-14" />
          </Stack>
        )}
        {importBehavior === ApiImportGlobalUpdateBehavior.update_only && (
          <Stack className="card-panel" gap="2xl">
            <Stack gap="sm">
              <Skeleton className="w-48 h-7" />
              <Skeleton className="w-72 h-5" />
            </Stack>
            <Skeleton className="w-full h-14" />
            <Skeleton className="w-full h-14" />
          </Stack>
        )}
      </Stack>
    );
  }

  const behaviorInfo = getBehaviorInformation(importBehavior);

  return (
    <Stack gap="sm">
      <Stack className="card-panel" gap="2xl">
        <Title
          size="lg"
          header={t("core:import_data_step_common_settings")}
          caption={t("import_data:common_settings_caption")}
        />
      </Stack>
      <Stack className="card-panel" gap="2xl">
        <Title
          size="md"
          header={t("import_data:how_to_match_records")}
          caption={t("import_data:how_to_match_records_caption")}
        />
        <Select
          value={matchingBy !== null ? String(matchingBy) : null}
          onChange={(value) =>
            setMatchingBy(value === null || value === undefined ? null : Number(value))
          }
          options={matchByOptions}
          emptyTitle={t("common:select")}
          showCaption
          searchable
        />
      </Stack>
      {importType === ApiImportDataType.custom_table && (
        <Stack className="card-panel" gap="2xl">
          <Title
            size="md"
            header={t("import_data:destination")}
            caption={t("import_data:destination_caption")}
          />
          <Select
            value={selectedCustomTableId ? String(selectedCustomTableId) : ""}
            onChange={(value) => {
              const tableId = value ? Number(value) : null;
              setSelectedCustomTableId(tableId);
              const tableName = customTableOptions.find((opt) => opt.id === value)?.title ?? null;
              setSelectedCustomTableName(tableName);
            }}
            options={customTableOptions}
            emptyTitle={t("common:select")}
          />
        </Stack>
      )}
      <Stack className="card-panel" gap="2xl">
        <Title
          size="md"
          header={t("import_data:how_to_handle_new_records")}
          caption={t("import_data:how_to_handle_new_records_caption")}
        />
        <Stack className="border border-dark/5 rounded-sm py-2 px-4 bg-dark/5">
          <Radio
            value={importBehavior}
            checked
            title={behaviorInfo.title}
            caption={behaviorInfo.caption}
            onChange={() => {}}
          />
        </Stack>
      </Stack>
      {importBehavior === ApiImportGlobalUpdateBehavior.update_only && (
        <Stack className="card-panel" gap="2xl">
          <Title
            size="md"
            header={t("import_data:default_update_method")}
            caption={t("import_data:default_update_method_caption")}
          />
          <Stack className="border border-dark/5 rounded-sm py-2 px-4 bg-dark/5">
            <Radio
              value={ApiImportFieldUpdateBehavior.only_if_empty}
              checked={defaultUpdateBehavior === ApiImportFieldUpdateBehavior.only_if_empty}
              title={t("import_data:update_only_if_empty")}
              caption={t("import_data:update_only_if_empty_caption")}
              onChange={() => setDefaultUpdateBehavior(ApiImportFieldUpdateBehavior.only_if_empty)}
            />
            <Radio
              value={ApiImportFieldUpdateBehavior.always}
              checked={defaultUpdateBehavior === ApiImportFieldUpdateBehavior.always}
              title={t("import_data:update_always")}
              caption={t("import_data:update_always_caption")}
              onChange={() => setDefaultUpdateBehavior(ApiImportFieldUpdateBehavior.always)}
            />
          </Stack>
        </Stack>
      )}

      <Button size="lg" onClick={handleNext}>
        {nextButtonText}
      </Button>
    </Stack>
  );
}
